import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Input, Spinner } from "reactstrap";
import ImportProsprEmployeeReportModal from "./ImportProsprEmployeeReportModal";
import ImportLaborSpreadsheetReportModal from "./ImportLaborSpreadsheetReportModal";
import ImportSpotHeroReportModal from "./ImportSpotHeroReportModal";
import ImportSureParkReportModal from "./ImportSureParkReportModal";
import DateRangeSelector from "components/Common/DateRangeSelector";
import ImportReportSelectBox from "./ImportReportSelectBox";
import ImportAmanoReportModal from "./ImportAmanoReportModal";
import { loadReportsHelper } from "helpers/loadReports";
import LocationExpandableCard from "./LocationExpandableCard";
import formatCurrency from "constants/formatCurrency";

const fmt = (d) => d.toISOString().slice(0, 10);

const checkboxWrap = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: 10,
    background: "#3a3a3a",
    padding: "10px 14px",
    borderRadius: "12px",
    cursor: "pointer",
    border: "1px solid rgba(255,255,255,0.12)",
};

const checkboxBox = (checked) => ({
    width: 18,
    height: 18,
    borderRadius: 5,
    border: checked ? "2px solid #ffd86b" : "2px solid #888",
    background: checked ? "#ffd86b" : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#000",
    fontSize: 12,
    fontWeight: 700,
});


const ReportListLocationBasedView = () => {
    const INCLUDED_OPTIONS = [
        "corepark",
        "prospr",
        "amano",
        "spothero",
        "surepark",
        "resolve",
        "monthly_parking",
        "events",
    ];

    const user = JSON.parse(localStorage.getItem("authUser"));

    const defaults = useMemo(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { start: fmt(start), end: fmt(end) };
    }, []);

    const [startDate, setStartDate] = useState(defaults.start);
    const [endDate, setEndDate] = useState(defaults.end);
    const [reports, setReports] = useState({});
    const [expanded, setExpanded] = useState({});
    const [loading, setLoading] = useState(false);
    const [importProsprEmployeeReportModal, setImportProsprEmployeeReportModal] = useState(false);
    const [importLaborSpreadsheetReportModal, setImportLaborSpreadsheetReportModal] = useState(false);
    const [importAmanoReportModal, setImportAmanoReportModal] = useState(false);
    const [importSpotHeroReportModal, setImportSpotHeroReportModal] = useState(false);
    const [importSureParkReportModal, setImportSureParkReportModal] = useState(false);
    const [selectedManager, setSelectedManager] = useState("All Managers");
    const [selectedStructure, setSelectedStructure] = useState("All Types");
    const [progressMessages, setProgressMessages] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [included, setIncluded] = useState(["resolve"]);
    const [filterBySource, setFilterBySource] = useState(false);
    const [sortOrder, setSortOrder] = useState("AZ");

    const hasSourceData = (data, source) => {
        switch (source) {
            case "corepark": {
                const cp = data.corepark || {};
                return (
                    (cp.cash || 0) +
                    (cp.card || 0) +
                    (cp.tickets || 0) +
                    (cp.fee || 0) +
                    (cp.tax || 0)
                ) > 0;
            }

            case "prospr":
                return (data.prospr?.hours || 0) > 0;

            case "amano":
                return (data.amano?.fee || 0) +
                    (data.amano?.transactions || 0) > 0;

            case "spothero":
                return (data.spothero?.amount || 0) > 0;

            case "surepark":
                return (data.surepark?.amount || 0) > 0;

            case "resolve":
                return (data.resolve?.amount || 0) > 0;

            case "monthly_parking":
                return (data.monthly_parking_orders?.amount || 0) +
                    (data.monthly_parking_orders?.orders || 0) > 0;

            case "events":
                return (data.events?.income || 0) +
                    (data.events?.events || 0) > 0;

            default:
                return false;
        }
    };

    const sortedReports = Object.entries(reports)
        .filter(([loc, platforms]) => {
            if (selectedManager !== "All Managers" &&
                (platforms.account_manager || "").toLowerCase() !== selectedManager.toLowerCase()) {
                return false;
            }
            if (selectedStructure !== "All Types" &&
                (platforms.operating_structure || "").toLowerCase() !== selectedStructure.toLowerCase()) {
                return false;
            }
            if (
                searchTerm &&
                !loc.toLowerCase().includes(searchTerm.toLowerCase())
            ) {
                return false;
            }
            if (filterBySource) {
                const activeSources = included.includes("all")
                    ? INCLUDED_OPTIONS
                    : included;

                const hasAny = activeSources.some(src =>
                    hasSourceData(platforms, src)
                );

                if (!hasAny) return false;
            }
            return true;
        })
        .sort(([a], [b]) => {
            if (sortOrder === "AZ") return a.localeCompare(b);
            return b.localeCompare(a);
        });

    const loadReports = async (start, end) => {
        setLoading(true);
        setProgressMessages([]);
        try {
            const reportsObj = await loadReportsHelper({
                token: user.token,
                start,
                end,
                included: included.includes("all") ? ["all"] : included,
                selected: [{ value: "All Locations" }],
                setProgressMessages,
                setLocations: () => { },
                setActiveTab: () => { },
            });
            setReports(reportsObj || {});
            setExpanded({});
        } finally {
            setLoading(false);
            setProgressMessages([]);
        }
    };

    const toggleExpand = (location) => {
        setExpanded((prev) => ({ ...prev, [location]: !prev[location] }));
    };

    const toggleIncluded = (key) => {
        setIncluded(prev => {
            if (key === "all") return ["all"];

            const withoutAll = prev.filter(i => i !== "all");

            if (withoutAll.includes(key)) {
                return withoutAll.filter(i => i !== key);
            }

            return [...withoutAll, key];
        });
    };

    const accountManagers = useMemo(() => {
        const managers = new Set();
        Object.values(reports).forEach((platforms) => {
            if (platforms.account_manager) {
                managers.add(platforms.account_manager);
            }
        });
        return ["All Managers", ...Array.from(managers)];
    }, [reports]);

    const summary = useMemo(() => {
        const rows = sortedReports.map(([, data]) => data);

        const locations = rows.length;

        const estMonthly = rows.reduce(
            (sum, d) => sum + (d.daily_lease_amount || 0) * 30,
            0
        );

        const totalBillables = rows.reduce(
            (sum, d) => sum + (d.fees?.amount || 0),
            0
        );

        const lotExpenses = rows.reduce(
            (sum, d) =>
                sum +
                (d.costs?.payments || [])
                    .filter(p => p.type === "Parking Lot Cost")
                    .reduce((s, p) => s + (p.amount || 0), 0),
            0
        );

        const totalLabor = rows.reduce(
            (sum, d) => sum + (d.prospr?.cost || 0),
            0
        );

        // Sum of all costs, minus the lot expenses calculated above
        const totalPayables = rows.reduce(
            (sum, d) => sum + (d.costs?.amount || 0),
            0
        ) - lotExpenses;

        let lease = 0;
        let nonLease = 0;

        rows.forEach(d => {
            if ((d.operating_structure || "").toLowerCase() === "lease") lease++;
            else nonLease++;
        });

        const totalDataSources = rows.reduce((sum, d) => {
            const isLease = (d.operating_structure || "").toLowerCase() === "lease";

            // If it's a lease location, ignore CorePark (income comes from lease).
            // If it's NOT a lease location, include CorePark.
            const coreparkIncome = isLease
                ? 0
                : (d.corepark?.cash || 0) + (d.corepark?.card || 0);

            return (
                sum +
                coreparkIncome +
                (d.spothero?.amount || 0) +
                (d.surepark?.amount || 0) +
                (d.resolve?.amount || 0) +
                (d.events?.income || 0) +
                (d.monthly_parking_orders?.amount || 0)
            );
        }, 0);

        const totalRevenue = totalBillables + estMonthly + totalDataSources;
        const totalExpense = totalPayables + lotExpenses + totalLabor;
        const netProfit = totalRevenue - totalExpense;

        return {
            locations,
            estMonthly,
            totalBillables,
            totalDataSources,
            lotExpenses,
            totalLabor,
            totalPayables,
            lease,
            nonLease,
            totalRevenue,
            totalExpense,
            netProfit
        };
    }, [sortedReports]);

    return (
        <div>
            <Row className="justify-content-center my-3">
                <ImportReportSelectBox
                    loading={loading}
                    setImportProsprEmployeeReportModal={setImportProsprEmployeeReportModal}
                    setImportLaborSpreadsheetReportModal={setImportLaborSpreadsheetReportModal}
                    setImportSpotHeroReportModal={setImportSpotHeroReportModal}
                    setImportSureParkReportModal={setImportSureParkReportModal}
                    setImportAmanoReportModal={setImportAmanoReportModal}
                />

                <Col xs="12" md="auto">
                    <DateRangeSelector
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        onApply={(s, e) => loadReports(s, e)}
                    />
                </Col>
                <Col xs="12" md="auto">
                    <Input
                        type="select"
                        className="form-select form-select-lg accordion-gradient-border"
                        value={selectedManager}
                        onChange={(e) => setSelectedManager(e.target.value)}
                    >
                        {accountManagers.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </Input>
                </Col>
                <Col xs="12" md="auto">
                    <Input
                        type="select"
                        className="form-select form-select-lg accordion-gradient-border"
                        value={selectedStructure}
                        onChange={(e) => setSelectedStructure(e.target.value)}
                    >
                        <option key={"All Types"} value={"All Types"}>All Types</option>
                        <option key={"Lease"} value={"Lease"}>Lease</option>
                        <option key={"Non Lease"} value={"Non Lease"}>Non Lease</option>
                    </Input>
                </Col>
                <Col xs="12" md="auto">
                    <div
                        onClick={() =>
                            setSortOrder(prev => (prev === "AZ" ? "ZA" : "AZ"))
                        }
                        style={{
                            background: "#3a3a3a",
                            border: "1px solid rgba(255,255,255,0.12)",
                            borderRadius: 14,
                            padding: "12px 18px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            color: "#fff",
                            fontWeight: 700,
                        }}
                    >
                        {sortOrder === "AZ" ? "A–Z" : "Z–A"}
                    </div>
                </Col>

                <Col xs="12" style={{ marginTop: 20 }}>
                    <div
                        style={{
                            background: "#2f2f2f",
                            borderRadius: 14,
                            padding: "16px 18px",
                        }}
                    >
                        <div
                            style={{
                                color: "#ffd86b",
                                fontWeight: 700,
                                marginBottom: 10,
                            }}
                        >
                            Include Data Sources
                        </div>

                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                            {INCLUDED_OPTIONS.map(opt => {
                                const checked = included.includes(opt);
                                return (
                                    <div
                                        key={opt}
                                        style={checkboxWrap}
                                        onClick={() => toggleIncluded(opt)}
                                    >
                                        <div style={checkboxBox(checked)}>
                                            {checked ? "✓" : ""}
                                        </div>
                                        <div style={{ color: "#fff", textTransform: "capitalize" }}>
                                            {opt.replace("_", " ")}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div
                            style={{
                                marginTop: 16,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 12,
                            }}
                        >
                            <div
                                style={checkboxWrap}
                                onClick={() => setFilterBySource(!filterBySource)}
                            >
                                <div style={checkboxBox(filterBySource)}>
                                    {filterBySource ? "✓" : ""}
                                </div>
                                <div style={{ color: "#fff", fontSize: 14 }}>
                                    Filter results for data sources
                                </div>
                            </div>

                            <button
                                onClick={() => loadReports(startDate, endDate)}
                                style={{
                                    background: "#ffd86b",
                                    color: "#000",
                                    fontWeight: 700,
                                    border: "none",
                                    borderRadius: 10,
                                    padding: "10px 18px",
                                    cursor: "pointer",
                                }}
                            >
                                Refresh
                            </button>
                        </div>
                    </div>
                </Col>

                <Col xs="12" md={9}>
                    <Input
                        placeholder="Search locations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: "75%",
                            margin: "0 auto",
                            marginTop: 15,
                            background: "#3a3a3a",
                            border: "1px solid rgba(255,255,255,0.12)",
                            color: "#fff",
                            borderRadius: 14,
                            padding: "14px 18px",
                            fontSize: 16,
                        }}
                    />
                </Col>

                <Col xs="12" style={{ marginTop: 20 }}>
                    <div
                        style={{
                            background: "#2f2f2f",
                            borderRadius: 14,
                            padding: "18px 20px",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 16,
                                justifyContent: "center",
                                width: "100%",
                            }}
                        >
                            {/* LOCATIONS */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    LOCATIONS
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>
                                    {summary.locations}
                                </div>
                            </div>

                            {/* EST MONTHLY */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    EST. LEASE MONTHLY
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 800, color: "#a3c5fbff" }}>
                                    ${formatCurrency(summary.estMonthly)}
                                </div>
                            </div>

                            {/* TOTAL BILLABLES */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    TOTAL BILLABLES
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 800, color: "#5FD38D" }}>
                                    ${formatCurrency(summary.totalBillables)}
                                </div>
                            </div>

                            {/* LOT EXPENSES */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    LOT EXPENSES
                                </div>
                                <div
                                    style={{
                                        fontSize: 28,
                                        fontWeight: 800,
                                        color: "#f87171",
                                    }}
                                >
                                    ${formatCurrency(summary.lotExpenses)}
                                </div>
                            </div>

                            {/* TOTAL DATA SOURCES */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        marginBottom: 6,
                                        color: "#ffd86b",
                                        fontWeight: 700,
                                    }}
                                >
                                    TOTAL DATA SOURCES
                                </div>
                                <div
                                    style={{
                                        fontSize: 28,
                                        fontWeight: 800,
                                        color: "#22C55E",
                                    }}
                                >
                                    ${formatCurrency(summary.totalDataSources)}
                                </div>
                            </div>

                            {/* TOTAL LABOR */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    TOTAL LABOR
                                </div>
                                <div
                                    style={{
                                        fontSize: 28,
                                        fontWeight: 800,
                                        color: "#fb923c",
                                    }}
                                >
                                    ${formatCurrency(summary.totalLabor)}
                                </div>
                            </div>

                            {/* TOTAL PAYABLES */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    TOTAL PAYABLES
                                </div>
                                <div
                                    style={{
                                        fontSize: 28,
                                        fontWeight: 800,
                                        color: "#f472b6",
                                    }}
                                >
                                    ${formatCurrency(summary.totalPayables)}
                                </div>
                            </div>

                            {/* STRUCTURE */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 320,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    STRUCTURE
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>
                                    {summary.nonLease} Non-Lease / {summary.lease} Lease
                                </div>
                            </div>

                            {/* TOTAL REVENUE */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    TOTAL REVENUE
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>
                                    ${formatCurrency(summary.totalRevenue)}
                                </div>
                            </div>

                            {/* TOTAL EXPENSE */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    TOTAL EXPENSE
                                </div>
                                <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>
                                    ${formatCurrency(summary.totalExpense)}
                                </div>
                            </div>

                            {/* NET PROFIT */}
                            <div
                                style={{
                                    background: "rgba(0,0,0,0.35)",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    minWidth: 220,
                                    textAlign: "center",
                                }}
                            >
                                <div style={{ fontSize: 12, marginBottom: 6, color: "#ffd86b", fontWeight: 700 }}>
                                    NET PROFIT
                                </div>
                                <div
                                    style={{
                                        fontSize: 28,
                                        fontWeight: 800,
                                        color: summary.netProfit >= 0 ? "#22C55E" : "#f87171",
                                    }}
                                >
                                    ${formatCurrency(summary.netProfit)}
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {loading ? (
                <div className="d-flex justify-content-center align-items-center my-5 flex-column">
                    <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                    {progressMessages.map((msg, i) => (
                        <div key={i} style={{ marginTop: 10, fontSize: 16, color: "#555" }}>
                            {msg}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                        {sortedReports.map(([location, platforms]) => (
                            <LocationExpandableCard
                                key={location}
                                location={location}
                                data={platforms}
                                expanded={expanded[location]}
                                onToggle={() => toggleExpand(location)}
                            />
                        ))}
                    </div>
                </div>
            )}

            <ImportProsprEmployeeReportModal
                isOpen={importProsprEmployeeReportModal}
                toggle={() => setImportProsprEmployeeReportModal(!importProsprEmployeeReportModal)}
            />

            <ImportLaborSpreadsheetReportModal
                isOpen={importLaborSpreadsheetReportModal}
                toggle={() => setImportLaborSpreadsheetReportModal(!importLaborSpreadsheetReportModal)}
            />

            <ImportAmanoReportModal
                isOpen={importAmanoReportModal}
                toggle={() => setImportAmanoReportModal(!importAmanoReportModal)}
            />

            <ImportSpotHeroReportModal
                isOpen={importSpotHeroReportModal}
                toggle={() => setImportSpotHeroReportModal(!importSpotHeroReportModal)}
            />

            <ImportSureParkReportModal
                isOpen={importSureParkReportModal}
                toggle={() => setImportSureParkReportModal(!importSureParkReportModal)}
            />
        </div>
    );
};

export default ReportListLocationBasedView;